import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  OnInit,
} from "@angular/core";
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { BehaviorSubject, Observer } from "rxjs";
import { Router } from "@angular/router";

import { ByPriority } from "../../../../model/filters/by-priority";
import { MatchAny } from "../../../../model/filters/match.any";
import { Filter } from "../../../../model/filters/filter";
import { SingleChoice } from "../../../../model/singlechoice";
import { PrioritiesService } from "../../../../services/priorities.service";
import { NavigationHelper } from "../../../../services/navigation.helper";
import { FilterService } from "../../../../services/filter.service";
import { Navigation } from "../../../../model/navigation";
import { Labeler } from "../../../../model/labeler";
import { NotComponent } from "../not/not.component";

@Component({
  selector: "by-priority-form",
  templateUrl: "./by-priority-form.component.html",
  styleUrl: "./by-priority-form.component.css",
})
export class ByPriorityFormComponent implements OnInit {
  @Input() filter: ByPriority = new ByPriority();
  @Output() applyFilter = new EventEmitter<ByPriority>();
  @Output() choiceEvent: EventEmitter<SingleChoice> = new EventEmitter();

  byPriorityForm: FormGroup;

  editingIndex: number | null = null;
  priorityLabeler = new BehaviorSubject<string>("-");
  completionUrl = new BehaviorSubject<string>("tickets/-1/filter/by.priority");
  currentId: number | null = null;

  byPriorityFilter: ByPriority = new ByPriority();
  @ViewChild(NotComponent) notComponent!: NotComponent;

  constructor(
    private fb: FormBuilder,
    private priorityService: PrioritiesService,
    private nav: NavigationHelper,
    private filterService: FilterService,
    private router: Router
  ) {}

  ngOnInit(): void {
    const existingFilters =
      this.filter instanceof MatchAny ? this.filter.filters : [this.filter];

    const priorityIds: number[] = existingFilters
      .filter((f): f is ByPriority => f instanceof ByPriority)
      .map((f: ByPriority) => f.id)
      .filter((id): id is number => id !== undefined);

    const storedPriorityIds = localStorage.getItem("priorityIds");
    const priorityIdsFromStorage = storedPriorityIds
      ? (JSON.parse(storedPriorityIds) as number[])
      : [];

    const mergedIds = Array.from(
      new Set([...priorityIds, ...priorityIdsFromStorage])
    );

    this.initializeForm(mergedIds);

    if (mergedIds.length > 0) {
      this.initializeForm(mergedIds);
    } else {
      this.byPriorityForm = this.fb.group({
        priorities: this.fb.array([this.fb.control(null, Validators.required)]),
      });
    }

    if (mergedIds.length > 0) {
      this.currentId = mergedIds[0];
      this.priorityLabeler.next(
        this.priorityService.labelForId(this.currentId)
      );
    }
  }

  initializeForm(priorityIds: number[]): void {
    const controls = priorityIds.map((id) =>
      this.fb.control(id, Validators.required)
    );
    if (controls.length === 0)
      controls.push(this.fb.control(null, Validators.required));

    this.byPriorityForm = this.fb.group({
      priorities: this.fb.array(controls),
    });

    this.currentId = controls[0].value ?? null;
    if (this.currentId !== null) {
      this.priorityLabeler.next(
        this.priorityService.labelForId(this.currentId)
      );
    }
  }

  restorePriorities(ids: (number | null)[]): void {
    this.byPriorityForm.setControl("priorities", this.fb.array([]));
    for (const id of ids) {
      this.priorities.push(this.fb.control(id, Validators.required));
    }
  }

  apply(): void {
    const ids: number[] = this.priorities.value.filter(
      (id: number | null | undefined): id is number =>
        id !== null && id !== undefined
    );

    if (ids.length === 0) return;

    localStorage.setItem("priorityIds", JSON.stringify(ids));

    let finalFilter: Filter;

    if (ids.length === 1) {
      finalFilter = new ByPriority(ids[0]);
    } else {
      finalFilter = new MatchAny(ids.map((id) => new ByPriority(id)));
    }

    this.notComponent.filter = finalFilter;
    const modifiedFilter: Filter = this.notComponent.getModifiedFilter();

    this.applyFilter.emit(modifiedFilter);

  }

  onFilterChanged(finalFilter: Filter) {
    this.applyFilter.emit(finalFilter);
 
  }

  relaySingleChoice(choice: SingleChoice) {
    this.nav.startChoice(choice);
  }

  onPriorityChoice(id: number): void {
    if (this.editingIndex !== null && this.priorities.at(this.editingIndex)) {
      this.priorities.at(this.editingIndex).setValue(id);
    } else if (this.priorities.length > 0) {
      this.priorities.at(0).setValue(id);
    }

    const currentValues = this.priorities.value.filter((id) => id !== null);
    localStorage.setItem("priorityIds", JSON.stringify(currentValues));

    this.currentId = id;

    this.router.navigate(["tickets/-1/filter/by.priority/" + id]).then(() => {
      this.nav.freshNavigation;
    });
  }

  addPriorityField(): void {
    this.priorities.push(this.fb.control(null, Validators.required));

    const currentValues = this.priorities.value.filter((id) => id !== null);
    localStorage.setItem("priorityIds", JSON.stringify(currentValues));
  }

  setEditingIndex(index: number): void {
    this.editingIndex = index;
    localStorage.setItem("editingPriorityIndex", index.toString());
  }

  get priorities(): FormArray<FormControl> {
    return this.byPriorityForm.get("priorities") as FormArray<FormControl>;
  }

  get priorityObserver(): Partial<Observer<number>> {
    return {
      next: (id) => {
        this.onPriorityChoice(id);
      },
    };
  }

  get fakeId(): number {
    return Navigation.NEW;
  }

  get priorityLabelerByService(): Labeler {
    return this.priorityService;
  }
}
